import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from "react";
import { createEVMClient } from "@metamask/connect-evm";
import { Button, IconButton, Link, Portal, Snackbar, useMediaQuery } from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { MainChainID } from "../utils/constants";
import { Web3Provider } from "@ethersproject/providers";
import { toHex } from "web3-utils";
import { consoleLog } from "../utils/methods";

type EVMClient = Awaited<ReturnType<typeof createEVMClient>>;

// ---- Mobile-web deep-link handoff ---------------------------------------------------------
// In a mobile browser (Chrome/Safari, no injected provider) the Connect SDK talks to the
// MetaMask app over its relay and brings the app forward with a metamask:// deep link. Browsers
// only launch an external app from a navigation that still carries the user's tap ("Chrome
// won't launch an app from a JavaScript timer without a user gesture" —
// developer.chrome.com/docs/android/intents). The SDK fires its links after async work: relay
// and key setup on connect, a 10ms timer on every wallet request, and no tap at all for the
// personal_sign that follows connect once the user is back in the browser. When the launch is
// blocked nothing visible happens and the user is left on a spinner.
//
// So every SDK link goes through openWalletLink: it launches in place (the SDK asks for
// "_self"; a "_blank" window is popup-blocked without activation and strands the dapp tab
// behind a blank one), then, if the page is still visible shortly after, raises a
// "Continue in MetaMask" prompt. Tapping it is a fresh gesture, so that launch always goes.
type WalletLinkState = { link: string | null; prompt: boolean };

let walletLinkState: WalletLinkState = { link: null, prompt: false };
const walletLinkListeners = new Set<() => void>();
let launchCheckTimer: ReturnType<typeof setTimeout> | undefined;
// connect/connectAndSign calls in flight on the SDK path. While > 0, coming back to the page
// with no answer yet re-arms the prompt (e.g. the user backed out of MetaMask, or the sign
// request after connect couldn't open the app).
let trackedWalletActions = 0;
// An app switch hides the page well within this; still visible after it ⇒ the launch was blocked.
const LAUNCH_CHECK_MS = 1500;

const setWalletLinkState = (next: Partial<WalletLinkState>) => {
  walletLinkState = { ...walletLinkState, ...next };
  walletLinkListeners.forEach((listener) => listener());
};

const subscribeWalletLink = (listener: () => void) => {
  walletLinkListeners.add(listener);
  return () => {
    walletLinkListeners.delete(listener);
  };
};

const armLaunchCheck = () => {
  clearTimeout(launchCheckTimer);
  launchCheckTimer = setTimeout(() => {
    if (walletLinkState.link && document.visibilityState === "visible") {
      setWalletLinkState({ prompt: true });
    }
  }, LAUNCH_CHECK_MS);
};

const openWalletLink = (link: string) => {
  setWalletLinkState({ link, prompt: false });
  // Same-tab navigation to a custom scheme hands off to the app without unloading the page.
  window.location.href = link;
  armLaunchCheck();
};

const handleWalletLinkVisibility = () => {
  if (document.visibilityState === "hidden") {
    // The app came forward — the launch worked.
    clearTimeout(launchCheckTimer);
    if (walletLinkState.prompt) {
      setWalletLinkState({ prompt: false });
    }
  } else if (trackedWalletActions > 0 && walletLinkState.link) {
    armLaunchCheck();
  }
};

const trackWalletAction = async <T,>(action: () => Promise<T>): Promise<T> => {
  trackedWalletActions += 1;
  try {
    return await action();
  } finally {
    trackedWalletActions -= 1;
    if (trackedWalletActions === 0) {
      clearTimeout(launchCheckTimer);
      setWalletLinkState({ link: null, prompt: false });
    }
  }
};

const WalletLinkPrompt = () => {
  const { link, prompt } = useSyncExternalStore(subscribeWalletLink, () => walletLinkState);
  const reduceMotion = useMediaQuery("(prefers-reduced-motion: reduce)");

  // Portaled so it lands outside the app root: an open MUI Dialog (the login dialog) marks the
  // root aria-hidden, which would hide the prompt from assistive tech.
  return (
    <Portal>
      <Snackbar
        open={prompt && !!link}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        transitionDuration={reduceMotion ? 0 : undefined}
        message={
          <>
            Continue in the MetaMask app to approve the request.{" "}
            <Link href="https://metamask.io/download/" target="_blank" rel="noopener noreferrer" color="inherit">
              Get MetaMask
            </Link>
          </>
        }
        action={
          <>
            <Button color="primary" size="small" variant="contained" onClick={() => link && openWalletLink(link)}>
              Open MetaMask
            </Button>
            <IconButton
              aria-label="Dismiss"
              color="inherit"
              size="small"
              onClick={() => setWalletLinkState({ prompt: false })}
            >
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </>
        }
      />
    </Portal>
  );
};

// Module-level singleton — createEVMClient must not be called more than once per session
let clientInitPromise: Promise<EVMClient> | null = null;

export function getOrCreateClient(): Promise<EVMClient> {
  if (!clientInitPromise) {
    const chainHex = toHex(MainChainID) as `0x${string}`;
    const rpcUrl = process.env.REACT_APP_RPC_URL || "https://data-seed-prebsc-1-s1.bnbchain.org:8545";
    clientInitPromise = createEVMClient({
      dapp: {
        name: "EcoFutures Marketplace",
        url: window.location.origin,
      },
      api: {
        supportedNetworks: { [chainHex]: rpcUrl } as Record<`0x${string}`, string>,
      },
      // Disable dapp-side analytics events and wallet correlation metadata. The SDK otherwise
      // streams a steady flow of messages to the MetaMask extension, which leaks memory in the
      // extension process. Requires @metamask/connect-evm >= 1.4.0.
      analytics: {
        enabled: false,
      },
      ui: {
        preferExtension: true,
      },
      // Mobile web only (the SDK calls this when there's no extension/in-app provider).
      // metamask:// rather than the metamask.app.link universal link: a universal link that
      // isn't intercepted loads in this tab and unloads the dapp mid-handshake.
      mobile: {
        useDeeplink: true,
        preferredOpenLink: (link: string) => openWalletLink(link),
      },
    });
  }
  return clientInitPromise!;
}

export type ConnectAndSignResult = {
  accounts: string[];
  chainId: string;
  signature: string;
};

export type MetaMaskContextType = {
  isActive: boolean;
  isConnected: boolean;
  isSdkReady: boolean;
  // True in a mobile browser without an injected provider: every wallet approval happens in the
  // MetaMask app over a deep link, so each one needs its own tap (see openWalletLink).
  isAppSwitchWallet: boolean;
  web3Provider: any;
  account: string | null | undefined;
  currentAccount: string;
  chainId: string | undefined;
  isLoading: boolean;
  isOnCorrectChain: boolean;
  connect: () => Promise<boolean | string>;
  // `message` may be a thunk so callers can fetch it (e.g. an auth challenge) while the wallet
  // connects, keeping that round-trip off the tap → deep-link path on mobile.
  connectAndSign: (message: string | (() => Promise<string>)) => Promise<ConnectAndSignResult | string>;
  disconnect: () => void;
  switchChain: () => Promise<boolean | string>;
  signer: (data: string) => Promise<string>;
  library: Web3Provider | undefined;
  waitForReady: (timeoutMs?: number) => Promise<boolean>;
};

export const MetaMaskContext = React.createContext<MetaMaskContextType | null>(null);

// True when an EIP-1193 provider is injected into the page — i.e. a desktop browser with the
// MetaMask extension, or the MetaMask mobile in-app browser.
const hasInjectedProvider = (): boolean => typeof window !== "undefined" && !!(window as any).ethereum;

// Wallets don't agree on the chainId shape: EIP-1193 says "0x61", but some providers (and
// older/proxied injections) hand back a number or a decimal string, or upper-case hex. Store
// one canonical lower-case hex form so isOnCorrectChain's string compare can't false-negative.
const normalizeChainId = (cId: unknown): string | undefined => {
  if (cId === null || cId === undefined || cId === "") return undefined;
  const raw = String(cId).trim().toLowerCase();
  const n = typeof cId === "number" ? cId : raw.startsWith("0x") ? parseInt(raw, 16) : parseInt(raw, 10);
  return Number.isFinite(n) ? "0x" + n.toString(16) : undefined;
};

export const MetaMaskProvider = ({ children }: any) => {
  const [client, setClient] = useState<EVMClient | null>(null);
  const [provider, setProvider] = useState<any>(null);
  const [accounts, setAccounts] = useState<string[]>([]);
  const [chainId, setRawChainId] = useState<string | undefined>(undefined);
  const setChainId = useCallback((cId: unknown) => setRawChainId(normalizeChainId(cId)), []);
  const [connecting, setConnecting] = useState(false);
  const [ready, setReady] = useState(false);
  const [isAppSwitchWallet, setIsAppSwitchWallet] = useState(false);

  const readyRef = useRef(false);
  // Whether we're using the injected provider (true) or the Connect SDK transport (false).
  const useInjectedRef = useRef(false);
  const providerRef = useRef<any>(null);
  const handlersRef = useRef<{
    accountsChanged: (accs: string[]) => void;
    chainChanged: (cId: string) => void;
    disconnect: () => void;
  } | null>(null);

  useEffect(() => {
    let mounted = true;

    // Wire up event listeners + connection restore on whichever EIP-1193 provider we end up with.
    const setupProvider = (prov: any) => {
      providerRef.current = prov;

      const handleAccountsChanged = (accs: string[]) => {
        setAccounts(accs || []);
        if (!accs || accs.length === 0) setChainId(undefined);
      };
      const handleChainChanged = (cId: string) => setChainId(cId);
      const handleDisconnect = () => {
        setAccounts([]);
        setChainId(undefined);
      };

      handlersRef.current = {
        accountsChanged: handleAccountsChanged,
        chainChanged: handleChainChanged,
        disconnect: handleDisconnect,
      };

      prov.on("accountsChanged", handleAccountsChanged);
      prov.on("chainChanged", handleChainChanged);
      prov.on("disconnect", handleDisconnect);

      setProvider(prov);
      setReady(true);
      readyRef.current = true;

      // Restore any existing connection without prompting the user
      prov
        .request({ method: "eth_accounts" })
        .then((result: unknown) => {
          const accs = result as string[];
          if (!mounted || !accs?.length) return null;
          setAccounts(accs);
          return prov.request({ method: "eth_chainId" });
        })
        .then((result: unknown) => {
          const cId = result as string | null;
          if (mounted && cId) setChainId(cId);
        })
        .catch(() => {});
    };

    // Re-read the chain whenever the tab regains focus. chainChanged is the primary signal,
    // but it can be missed (the switch happened in the wallet while this tab was backgrounded,
    // or a proxied/multi-wallet injection doesn't forward the event) — which left the
    // wrong-network banner stuck up while the wallet was already on the right chain.
    const resyncChainId = () => {
      if (document.visibilityState !== "visible" || !providerRef.current) return;
      providerRef.current
        .request({ method: "eth_chainId" })
        .then((cId: unknown) => {
          if (mounted && cId) setChainId(cId);
        })
        .catch(() => {});
    };
    window.addEventListener("focus", resyncChainId);
    document.addEventListener("visibilitychange", resyncChainId);

    if (hasInjectedProvider()) {
      // Desktop (and the MetaMask in-app browser): use the injected provider directly and skip
      // the Connect SDK. Its Firefox window.postMessage transport relays every message through
      // the extension content script, flooding the extension process until the OS OOM-kills it.
      useInjectedRef.current = true;
      setupProvider((window as any).ethereum);
    } else {
      // No injected provider (mobile browser without the extension): fall back to the Connect SDK,
      // which handles deep-linking into the MetaMask app.
      useInjectedRef.current = false;
      setIsAppSwitchWallet(true);
      document.addEventListener("visibilitychange", handleWalletLinkVisibility);
      getOrCreateClient()
        .then((c) => {
          if (!mounted) return;
          setClient(c);
          setupProvider(c.getProvider());
        })
        .catch((err) => {
          consoleLog("Failed to initialize MetaMask client", err);
        });
    }

    return () => {
      mounted = false;
      window.removeEventListener("focus", resyncChainId);
      document.removeEventListener("visibilitychange", resyncChainId);
      document.removeEventListener("visibilitychange", handleWalletLinkVisibility);
      if (providerRef.current && handlersRef.current) {
        providerRef.current.removeListener("accountsChanged", handlersRef.current.accountsChanged);
        providerRef.current.removeListener("chainChanged", handlersRef.current.chainChanged);
        providerRef.current.removeListener("disconnect", handlersRef.current.disconnect);
      }
    };
  }, []);

  const library = useMemo<Web3Provider | undefined>(() => {
    if (!provider) return undefined;
    try {
      return new Web3Provider(provider as any);
    } catch (error) {
      consoleLog("Failed to create Web3Provider", error);
      return undefined;
    }
  }, [provider]);

  const account = accounts[0] ?? null;
  const isConnected = accounts.length > 0;
  const isActive = isConnected;
  const isSdkReady = ready;
  const currentAccount = account ? account.toLowerCase() : "";
  const isOnCorrectChain = chainId === toHex(MainChainID);

  const waitForReady = useCallback(
    async (timeoutMs: number = 3000): Promise<boolean> => {
      if (readyRef.current) return true;

      return new Promise((resolve) => {
        const startTime = Date.now();
        const checkReady = () => {
          if (readyRef.current) {
            resolve(true);
          } else if (Date.now() - startTime > timeoutMs) {
            consoleLog("SDK ready timeout - proceeding anyway");
            resolve(false);
          } else {
            setTimeout(checkReady, 100);
          }
        };
        checkReady();
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const syncChainIdFrom = useCallback(
    async (prov: any) => {
      try {
        setChainId(await prov.request({ method: "eth_chainId" }));
      } catch {
        // Best effort — chainChanged will still land if the read fails.
      }
    },
    [setChainId]
  );

  const switchChain = useCallback(async (): Promise<boolean | string> => {
    if (!provider) return "Provider not available";

    try {
      await provider.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: toHex(MainChainID) }],
      });
      // Don't rely solely on chainChanged to clear the wrong-network state — read it back.
      await syncChainIdFrom(provider);
      return true;
    } catch (e: any) {
      if (e.code === 4902) {
        // Wallet doesn't know the chain — offer to add it, then the switch happens
        // implicitly (MetaMask switches to a newly added chain).
        try {
          await provider.request({
            method: "wallet_addEthereumChain",
            params: [
              {
                chainId: toHex(MainChainID),
                chainName: "BNB Smart Chain Testnet",
                nativeCurrency: { name: "Testnet BNB", symbol: "tBNB", decimals: 18 },
                rpcUrls: [process.env.REACT_APP_RPC_URL || "https://data-seed-prebsc-1-s1.bnbchain.org:8545"],
                blockExplorerUrls: ["https://testnet.bscscan.com"],
              },
            ],
          });
          await syncChainIdFrom(provider);
          return true;
        } catch (addErr: any) {
          if (addErr.code === 4001) {
            return "User rejected adding the network.";
          }
          return (
            `Network not found in MetaMask and adding it failed. ` +
            `Visit https://chainlist.org/chain/${MainChainID} to add the required network.`
          );
        }
      }
      if (e.code === 4001) {
        return "User rejected the network switch request.";
      }
      return (
        `Your wallet doesn't support required chain id ${MainChainID}. ` +
        `Visit https://chainlist.org/chain/${MainChainID} to connect the network.`
      );
    }
  }, [provider, syncChainIdFrom]);

  const connect = useCallback(async (): Promise<boolean | string> => {
    consoleLog("Connect function called");

    if (useInjectedRef.current) {
      if (!provider) {
        return "MetaMask is not available. Please install or unlock the extension and refresh.";
      }
      setConnecting(true);
      try {
        const accs = (await provider.request({ method: "eth_requestAccounts" })) as string[];
        if (!accs || accs.length === 0) {
          return "No accounts found. Please unlock MetaMask and try again.";
        }
        setAccounts(accs);

        const cId = (await provider.request({ method: "eth_chainId" })) as string;
        setChainId(cId);

        if (cId !== toHex(MainChainID)) {
          const switchResult = await switchChain();
          if (switchResult !== true) return switchResult;
        }
        return true;
      } catch (error: any) {
        consoleLog("Connect to MetaMask error", { error });
        if (error?.code === 4001) return "User rejected the connection request.";
        if (error?.message) return error.message;
        if (error?.name) return error.name;
        return "Failed to connect to MetaMask. Please try again.";
      } finally {
        setConnecting(false);
      }
    }

    consoleLog(`Client available: ${!!client}, Provider available: ${!!provider}`);

    // A tap can land before the init effect has stored the client — wait on the singleton
    // instead of failing.
    const sdkClient = client ?? (await getOrCreateClient().catch(() => null));
    if (!sdkClient) {
      return "MetaMask client is not initialized. Please refresh the page.";
    }

    setConnecting(true);
    try {
      // connect() returns accounts and chainId together in a single call
      const { accounts: connectedAccounts, chainId: connectedChainId } = await trackWalletAction(() =>
        sdkClient.connect({
          chainIds: [toHex(MainChainID) as `0x${string}`],
        })
      );

      consoleLog("client.connect() returned:", { connectedAccounts, connectedChainId });

      if (!connectedAccounts || connectedAccounts.length === 0) {
        return "No accounts found. Please unlock MetaMask and try again.";
      }

      setAccounts(connectedAccounts);
      setChainId(connectedChainId);

      consoleLog("Connection successful!");

      if (connectedChainId !== toHex(MainChainID)) {
        consoleLog("Wrong chain after connect, switching...");
        const switchResult = await switchChain();
        if (switchResult !== true) return switchResult;
      }

      return true;
    } catch (error: any) {
      consoleLog("Connect to MetaMask error", { error });

      if (error?.code === 4001) return "User rejected the connection request.";
      if (error?.message) return error.message;
      if (error?.name) return error.name;
      return "Failed to connect to MetaMask. Please try again.";
    } finally {
      setConnecting(false);
    }
  }, [client, provider, switchChain]);

  const disconnect = useCallback(async () => {
    try {
      // The injected provider has no programmatic disconnect — dropping local state is the norm.
      // The Connect SDK keeps a session open, so tear it down explicitly.
      if (!useInjectedRef.current && client) {
        await client.disconnect();
      }
    } catch (error) {
      consoleLog("Error on disconnect: ", error);
    }
    setAccounts([]);
    setChainId(undefined);
  }, [client]);

  const signer = useCallback(
    async (data: string): Promise<string> => {
      if (!provider) {
        throw new Error("Provider not available");
      }

      const signingAccount = currentAccount || account;
      consoleLog("Signing with account", signingAccount || "none");

      const sign = () =>
        provider.request({
          method: "personal_sign",
          params: [data, signingAccount],
        }) as Promise<string>;
      // On the SDK path, tracked so coming back without an answer re-arms the MetaMask prompt.
      return await (useInjectedRef.current ? sign() : trackWalletAction(sign));
    },
    [provider, currentAccount, account]
  );

  // connectAndSign: connect, then personal_sign, then fix the chain if needed.
  // On desktop / the MetaMask in-app browser the injected provider has no app-switch. On mobile
  // web (Connect SDK) this is two wallet round-trips — the SDK's own connectAndSign is likewise
  // connect() followed by a separate personal_sign — so each step's deep link goes through
  // openWalletLink, and the prompt covers the sign step whose link fires without a tap.
  // A thunk `message` is resolved only after the connect, so its fetch overlaps the connect
  // instead of delaying the first deep link past the tap's activation window.
  const connectAndSign = useCallback(
    async (message: string | (() => Promise<string>)): Promise<ConnectAndSignResult | string> => {
      const resolveMessage = () => (typeof message === "function" ? message() : Promise.resolve(message));
      setConnecting(true);
      try {
        if (useInjectedRef.current) {
          if (!provider) {
            return "MetaMask is not available. Please install or unlock the extension and refresh.";
          }
          const accs = (await provider.request({ method: "eth_requestAccounts" })) as string[];
          if (!accs || accs.length === 0) {
            return "No accounts found. Please unlock MetaMask and try again.";
          }
          setAccounts(accs);

          const signature = (await provider.request({
            method: "personal_sign",
            params: [await resolveMessage(), accs[0]],
          })) as string;

          let cId = (await provider.request({ method: "eth_chainId" })) as string;
          setChainId(cId);

          if (cId !== toHex(MainChainID)) {
            consoleLog("Wrong chain after connectAndSign, switching...");
            const switchResult = await switchChain();
            if (typeof switchResult === "string") return switchResult;
            cId = toHex(MainChainID);
          }

          return { accounts: accs, chainId: cId, signature };
        }

        const sdkClient = client ?? (await getOrCreateClient().catch(() => null));
        if (!sdkClient) {
          return "MetaMask client is not initialized. Please refresh the page.";
        }

        const {
          accounts: connectedAccounts,
          chainId: connectedChainId,
          signature,
        } = await trackWalletAction(async () => {
          const connected = await sdkClient.connect({ chainIds: [toHex(MainChainID) as `0x${string}`] });
          setAccounts([...connected.accounts]);
          setChainId(connected.chainId);

          const signed = (await sdkClient.getProvider().request({
            method: "personal_sign",
            params: [await resolveMessage(), connected.accounts[0]],
          })) as string;
          return { ...connected, signature: signed };
        });

        const newAccounts = [...connectedAccounts];
        consoleLog("connectAndSign succeeded", { accounts: newAccounts, chainId: connectedChainId });

        if (connectedChainId !== toHex(MainChainID)) {
          consoleLog("Wrong chain after connectAndSign, switching...");
          const switchResult = await switchChain();
          if (typeof switchResult === "string") return switchResult;
        }

        return {
          accounts: newAccounts,
          chainId: connectedChainId,
          signature,
        };
      } catch (error: any) {
        consoleLog("connectAndSign error", { error });
        if (error?.code === 4001) return "User rejected the request.";
        if (error?.message) return error.message;
        return "Failed to connect and sign. Please try again.";
      } finally {
        setConnecting(false);
      }
    },
    [client, provider, switchChain]
  );

  const values = useMemo(
    () => ({
      isActive,
      account,
      isLoading: connecting,
      web3Provider: provider,
      currentAccount,
      chainId,
      isConnected,
      isSdkReady,
      isAppSwitchWallet,
      isOnCorrectChain,
      connect,
      connectAndSign,
      library,
      signer,
      disconnect,
      switchChain,
      waitForReady,
    }),
    [
      isActive,
      connecting,
      account,
      currentAccount,
      chainId,
      isConnected,
      isSdkReady,
      isAppSwitchWallet,
      isOnCorrectChain,
      provider,
      library,
      connect,
      connectAndSign,
      signer,
      disconnect,
      switchChain,
      waitForReady,
    ]
  );

  return (
    <MetaMaskContext.Provider value={values}>
      {children}
      <WalletLinkPrompt />
    </MetaMaskContext.Provider>
  );
};

export default function useMetaMask(): MetaMaskContextType {
  const context = React.useContext(MetaMaskContext) as MetaMaskContextType;

  if (context === undefined) {
    throw new Error("useMetaMask hook must be used with a MetaMaskProvider component");
  }

  return context;
}
